import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, formatDate } from "@/lib/api";
import { Loading } from "@/components/common/States";
import { VerifiedTick } from "@/components/common/Badges";

const useAdminQuery = (key, url) => useQuery({ queryKey: [key], queryFn: async () => (await api.get(url)).data });

export function AdminReports() {
  const qc = useQueryClient();
  const { data, isLoading } = useAdminQuery("admin-reports", "/admin/reports");
  const act = async (id, action) => {
    try { await api.post(`/admin/reports/${id}`, { action }); toast.success("Bildirim sonuçlandırıldı"); qc.invalidateQueries({ queryKey: ["admin-reports"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  if (isLoading) return <Loading />;
  return (
    <div className="space-y-2" data-testid="admin-reports">
      {!data.length && <p className="text-sm text-zinc-500" data-testid="no-reports">Açık bildirim yok.</p>}
      {data.map((r) => (
        <div key={r.id} className="panel flex flex-wrap items-center justify-between gap-3 p-4 text-sm" data-testid={`report-row-${r.id}`}>
          <div><p><span className="rounded bg-black/[0.05] px-1.5 text-xs">{r.target_type}</span> <b>{r.reason}</b> — {r.reporter_name}</p>
            <p className="text-zinc-500">{r.target_preview?.title || r.target_preview?.full_name || r.target_preview?.body || r.target_preview?.comment}</p>
            {r.details && <p className="text-xs text-zinc-500">{r.details}</p>}</div>
          <div className="flex gap-2">
            <Button size="sm" className="bg-red-600" onClick={() => act(r.id, "hide")} data-testid={`report-hide-${r.id}`}>İçeriği kaldır</Button>
            <Button size="sm" variant="outline" className="bg-transparent" onClick={() => act(r.id, "dismiss")} data-testid={`report-dismiss-${r.id}`}>Yoksay</Button>
          </div>
        </div>
      ))}
      <Logs />
    </div>
  );
}

function Logs() {
  const { data = [] } = useAdminQuery("admin-logs", "/admin/logs");
  return (
    <details className="panel p-4 text-xs"><summary className="cursor-pointer text-zinc-500">Moderasyon kayıtları ({data.length})</summary>
      <ul className="mt-2 space-y-1">{data.map((l) => <li key={l.id}>{formatDate(l.created_at)} · {l.admin_name} · {l.action} · {l.target_type}:{l.target_id.slice(0, 8)} {l.note}</li>)}</ul></details>
  );
}

export function AdminUsers() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [nu, setNu] = useState({ email: "", full_name: "", phone: "", password: "" });
  const { data, isLoading } = useQuery({ queryKey: ["admin-users", q], queryFn: async () => (await api.get(`/admin/users?q=${encodeURIComponent(q)}`)).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-users"] });
  const call = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="panel grid gap-2 p-4 sm:grid-cols-5">
        <Input data-testid="admin-newuser-email" placeholder="E-posta" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} />
        <Input data-testid="admin-newuser-name" placeholder="İsim soyisim" value={nu.full_name} onChange={(e) => setNu({ ...nu, full_name: e.target.value })} />
        <Input data-testid="admin-newuser-phone" placeholder="Telefon" value={nu.phone} onChange={(e) => setNu({ ...nu, phone: e.target.value })} />
        <Input data-testid="admin-newuser-password" placeholder="Geçici şifre" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} />
        <Button data-testid="admin-newuser-submit" className="bg-[#0071e3] text-white" onClick={() => call(async () => { await api.post("/admin/users", nu); setNu({ email: "", full_name: "", phone: "", password: "" }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); }, "Kullanıcı oluşturuldu")}>Hesap oluştur</Button>
      </div>
      <Input data-testid="admin-users-search" placeholder="Kullanıcı ara" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" />
      {isLoading ? <Loading /> : (
        <div className="panel divide-y divide-black/[0.06]">
          {data.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm" data-testid={`admin-user-${u.id}`}>
              <div><p>{u.full_name} <VerifiedTick show={u.is_verified_seller} /> {u.is_super_admin && <span className="text-[10px] text-[#0077ed]">baş yönetici</span>}</p>
                <p className="text-xs text-zinc-500">{u.email} · {u.status}{u.locked ? " · kilitli" : ""}</p></div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-1 text-xs">Mavi tik<Switch data-testid={`blue-tick-${u.id}`} checked={!!u.is_verified_seller} onCheckedChange={(v) => call(() => api.post(`/admin/users/${u.id}/verify`, { value: v }), "Güncellendi")} /></label>
                {u.locked && <Button size="sm" variant="outline" className="bg-transparent" onClick={() => call(() => api.post(`/admin/users/${u.id}/unlock`), "Kilit açıldı")}>Kilidi aç</Button>}
                {!u.is_super_admin && <Button size="sm" variant="outline" className="bg-transparent text-red-700" data-testid={`ban-${u.id}`}
                  onClick={() => call(() => api.post("/admin/moderate", { target_type: "user", target_id: u.id, action: u.status === "banned" ? "restore" : "hide" }), "Güncellendi")}>{u.status === "banned" ? "Askıyı kaldır" : "Askıya al"}</Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminAdmins({ allPermissions }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [perms, setPerms] = useState([]);
  const { data, isLoading } = useAdminQuery("admin-admins", "/admin/admins");
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-admins"] });
  const grant = async (em, p) => { try { await api.post("/admin/admins", { email: em, permissions: p }); toast.success("Yetkiler kaydedildi"); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const revoke = async (id) => { try { await api.delete(`/admin/admins/${id}`); toast.success("Yetki kaldırıldı"); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const PermBoxes = ({ value, onChange, prefix }) => (
    <div className="flex flex-wrap gap-3">{Object.entries(allPermissions).map(([k, l]) => (
      <label key={k} className="flex items-center gap-1.5 text-xs"><Checkbox data-testid={`${prefix}-${k}`} checked={value.includes(k)} onCheckedChange={(c) => onChange(c ? [...value, k] : value.filter((x) => x !== k))} />{l}</label>))}</div>
  );
  if (isLoading) return <Loading />;
  return (
    <div className="space-y-4" data-testid="admin-admins">
      <div className="panel space-y-3 p-4">
        <p className="font-medium">Yetkili ekle (kullanıcı hesabı önceden açılmış olmalı)</p>
        <Input data-testid="grant-admin-email" placeholder="Kullanıcı e-postası" value={email} onChange={(e) => setEmail(e.target.value)} />
        <PermBoxes value={perms} onChange={setPerms} prefix="grant-perm" />
        <Button data-testid="grant-admin-submit" className="bg-[#0071e3] text-white" onClick={() => grant(email, perms)}>Yetki ver</Button>
      </div>
      {data.map((u) => (
        <div key={u.id} className="panel space-y-2 p-4 text-sm" data-testid={`admin-row-${u.id}`}>
          <div className="flex justify-between"><span>{u.full_name} · {u.email}</span>{u.is_super_admin ? <span className="text-xs text-[#0077ed]">Baş yönetici (tüm yetkiler)</span>
            : <Button size="sm" variant="outline" className="bg-transparent text-red-700" onClick={() => revoke(u.id)} data-testid={`revoke-${u.id}`}>Yetkiyi kaldır</Button>}</div>
          {!u.is_super_admin && <PermBoxes value={u.permissions || []} onChange={(p) => p.length && grant(u.email, p)} prefix={`perm-${u.id}`} />}
        </div>
      ))}
    </div>
  );
}

export function AdminSettings() {
  const qc = useQueryClient();
  const { data } = useAdminQuery("admin-settings", "/admin/settings");
  const { data: steps } = useAdminQuery("builder-steps", "/builder/steps");
  if (!data) return <Loading />;
  const save = async (patch) => {
    try { await api.put("/admin/settings", { seller_reviews_enabled: data.seller_reviews_enabled, fx_rates: data.fx_rates, ...patch }); toast.success("Ayarlar kaydedildi"); qc.invalidateQueries({ queryKey: ["admin-settings"] }); qc.invalidateQueries({ queryKey: ["public-settings"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleRule = async (id, value) => { await api.post(`/admin/compat-rules/${id}`, { value }); qc.invalidateQueries({ queryKey: ["builder-steps"] }); };
  return (
    <div className="space-y-4" data-testid="admin-settings">
      <label className="panel flex items-center justify-between p-4 text-sm"><span>Satıcı değerlendirme sistemi <span className="block text-xs text-zinc-500">Hazır, varsayılan olarak kapalı</span></span>
        <Switch data-testid="seller-reviews-switch" checked={data.seller_reviews_enabled} onCheckedChange={(v) => save({ seller_reviews_enabled: v })} /></label>
      <div className="panel flex flex-wrap items-center gap-3 p-4 text-sm"><span>Kur (₺ karşılığı, fiyat filtreleri için):</span>
        {Object.entries(data.fx_rates).map(([c, r]) => <label key={c} className="flex items-center gap-1">{c}<Input className="h-8 w-24" type="number" defaultValue={r} onBlur={(e) => save({ fx_rates: { ...data.fx_rates, [c]: Number(e.target.value) } })} /></label>)}</div>
      <div className="panel p-4 text-sm"><p className="mb-2 font-medium">Uyumluluk kuralları</p>
        {steps?.rules.map((r) => <label key={r.id} className="flex items-center justify-between border-t border-black/[0.08] py-1.5"><span><span className="font-mono text-xs text-zinc-500">{r.id}</span> {r.description}</span><Switch checked={r.enabled} onCheckedChange={(v) => toggleRule(r.id, v)} /></label>)}</div>
    </div>
  );
}
